import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Save,
  Plus,
  Trash2,
  Star,
  Shield,
  Car,
  Plane as PlaneIcon,
  Globe,
  Eye,
  EyeOff,
  Banknote,
  CheckCircle,
  ExternalLink,
  Loader2,
  Camera,
  X,
} from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { CountryMultiSelect, CountrySelect } from '@/components/CountrySelect'

// ─── Types ───────────────────────────────────────────────────────────────────

interface CrewProfile {
  id: string
  userId: string
  displayName: string
  primaryRole: string
  secondaryRoles: string[]
  baseCity: string
  baseCountry: string
  baseLat: number | null
  baseLng: number | null
  willingToTravel: boolean
  travelRadiusKm: number | null
  dayRateMinor: number | null
  currency: string
  bio: string | null
  headline: string | null
  videoUrl: string | null
  photoUrl: string | null
  websiteUrl: string | null
  unionMembership: string | null
  verified: boolean
  publicProfile: boolean
}

interface TravelDocuments {
  id: string
  passportHeld: boolean
  passportCountry: string | null
  passportExpiry: string | null
  secondPassportHeld: boolean
  secondPassportCountry: string | null
  secondPassportExpiry: string | null
  hasEntryRestrictions: boolean
  restrictedCountries: string[]
  drivingLicenceHeld: boolean
  drivingLicenceCountry: string | null
  drivingCategories: string[]
  additionalLicenceCountries: string[]
  internationalPermit: boolean
}

interface WorkHistoryEntry {
  id: string
  title: string
  artistName: string | null
  role: string
  startDate: string | null
  endDate: string | null
  city: string | null
  country: string | null
  description: string | null
  showArtistName: boolean
}

interface Testimonial {
  id: string
  fromName: string
  fromRole: string | null
  text: string
  rating: number | null
  eventName: string | null
  eventDate: string | null
  publicVisible: boolean
}

interface StripeStatus {
  connected: boolean
  onboardingComplete: boolean
  payoutsEnabled: boolean
  chargesEnabled?: boolean
}

interface CrewPaymentRecord {
  id: string
  amountPence: number
  feePence: number
  netPence: number
  currency: string
  description: string
  status: string
  paidAt: string | null
  createdAt: string
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SHOW_ROLES = [
  'principal_artist', 'musical_director', 'backing_singer', 'band_musician',
  'tour_manager', 'production_manager', 'personal_assistant', 'artist_security',
  'foh_engineer', 'monitor_engineer', 'playback_engineer', 'sound_tech',
  'lighting_director', 'lighting_operator', 'video_director', 'spotlight_operator',
  'stage_manager', 'backline_tech', 'guitar_tech', 'drum_tech',
  'wardrobe_supervisor', 'hair_makeup', 'catering_manager', 'medic',
  'head_rigger', 'rigger', 'stagehand', 'truck_driver',
  'catering_crew', 'dressing_room_runner', 'local_driver', 'interpreter',
  'loadout_crew', 'local_security',
] as const

const CURRENCIES = ['GBP', 'USD', 'EUR'] as const

const DRIVING_CATEGORIES = ['car', 'motorcycle', 'minibus', 'hgv'] as const

function roleLabel(role: string): string {
  return role.replace(/_/g, ' ')
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export function CrewProfilePage() {
  const { data, error, isLoading, refetch } = useQuery<CrewProfile | null, ApiClientError>({
    queryKey: ['crew-profile'],
    queryFn: () => apiFetch<CrewProfile | null>('/crew/profile'),
    retry: false,
  })

  const profileMissing = error?.status === 404 || (!isLoading && !error && data === null)

  if (profileMissing) {
    return <CrewProfileCreate onCreated={() => refetch()} />
  }

  return (
    <div>
      <PageHeader
        title="My crew profile"
        subtitle="Visible to artists searching the talent pool."
      />
      <AsyncBoundary isLoading={isLoading} error={profileMissing ? null : error}>
        {data && <CrewProfileFull profile={data} onSaved={() => refetch()} />}
      </AsyncBoundary>
    </div>
  )
}

// ─── Full Profile (6 sections) ───────────────────────────────────────────────

function CrewProfileFull({
  profile,
  onSaved,
}: {
  profile: CrewProfile
  onSaved: () => void
}) {
  return (
    <div className="space-y-8">
      <ProfileHeaderSection profile={profile} onSaved={onSaved} />
      <LocationAvailabilitySection profile={profile} onSaved={onSaved} />
      <TravelDocumentsSection />
      <WorkHistorySection />
      <TestimonialsSection />
      <StripePaymentsSection />
    </div>
  )
}

// ─── Section 1: Profile Header ───────────────────────────────────────────────

function ProfileHeaderSection({
  profile,
  onSaved,
}: {
  profile: CrewProfile
  onSaved: () => void
}) {
  const [displayName, setDisplayName] = useState(profile.displayName)
  const [headline, setHeadline] = useState(profile.headline ?? '')
  const [primaryRole, setPrimaryRole] = useState(profile.primaryRole)
  const [secondaryRoles, setSecondaryRoles] = useState<string[]>(profile.secondaryRoles)
  const [bio, setBio] = useState(profile.bio ?? '')
  const [videoUrl, setVideoUrl] = useState(profile.videoUrl ?? '')
  const [websiteUrl, setWebsiteUrl] = useState(profile.websiteUrl ?? '')
  const [unionMembership, setUnionMembership] = useState(profile.unionMembership ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setDisplayName(profile.displayName)
    setHeadline(profile.headline ?? '')
    setPrimaryRole(profile.primaryRole)
    setSecondaryRoles(profile.secondaryRoles)
    setBio(profile.bio ?? '')
    setVideoUrl(profile.videoUrl ?? '')
    setWebsiteUrl(profile.websiteUrl ?? '')
    setUnionMembership(profile.unionMembership ?? '')
  }, [profile])

  function toggleSecondaryRole(role: string) {
    setSecondaryRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    )
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      await apiFetch('/crew/profile', {
        method: 'PATCH',
        body: {
          displayName,
          headline: headline || undefined,
          primaryRole,
          secondaryRoles,
          bio: bio || undefined,
          videoUrl: videoUrl || undefined,
          websiteUrl: websiteUrl || undefined,
          unionMembership: unionMembership || undefined,
        },
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleSave}
      className="rounded-lg border border-ovation-ink/10 bg-white p-6"
    >
      <h2 className="mb-4 text-xs uppercase tracking-wider text-ovation-muted">
        Profile Header
      </h2>
      <ProfilePhotoField photoUrl={profile.photoUrl} onUploaded={onSaved} />
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field id="displayName" label="Display name" value={displayName} onChange={setDisplayName} required />
        <div className="sm:col-span-2">
          <Field
            id="headline"
            label="Headline"
            value={headline}
            onChange={setHeadline}
            hint="e.g. Session guitarist | 20 years touring"
          />
        </div>
        <div>
          <label htmlFor="primaryRole" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Primary role
          </label>
          <select
            id="primaryRole"
            value={primaryRole}
            onChange={(e) => setPrimaryRole(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            {SHOW_ROLES.map((r) => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
        </div>
        <div>
          <Field id="unionMembership" label="Union membership" value={unionMembership} onChange={setUnionMembership} />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Secondary roles
          </label>
          <div className="flex flex-wrap gap-2">
            {SHOW_ROLES.filter((r) => r !== primaryRole).map((r) => (
              <label
                key={r}
                className={`cursor-pointer rounded-md border px-2 py-1 text-xs transition-colors ${
                  secondaryRoles.includes(r)
                    ? 'border-ovation-ink bg-ovation-ink text-ovation-paper'
                    : 'border-ovation-ink/15 hover:border-ovation-ink/30'
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={secondaryRoles.includes(r)}
                  onChange={() => toggleSecondaryRole(r)}
                />
                {roleLabel(r)}
              </label>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="bio" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Bio
          </label>
          <textarea
            id="bio"
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <Field id="videoUrl" label="Video URL" value={videoUrl} onChange={setVideoUrl} hint="YouTube or Vimeo link" />
        <Field id="websiteUrl" label="Website URL" value={websiteUrl} onChange={setWebsiteUrl} />
      </div>
      <FormFooter saving={saving} error={error} saved={saved} />
    </form>
  )
}

const PROFILE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const
const MAX_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Could not read file'))
        return
      }
      resolve(reader.result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'))
    reader.readAsDataURL(file)
  })
}

function ProfilePhotoField({
  photoUrl,
  onUploaded,
}: {
  photoUrl: string | null
  onUploaded: () => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [useUrl, setUseUrl] = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [savingUrl, setSavingUrl] = useState(false)

  async function handleFile(file: File) {
    if (!PROFILE_PHOTO_TYPES.includes(file.type as (typeof PROFILE_PHOTO_TYPES)[number])) {
      setError('Please choose a JPEG, PNG, WebP, or GIF image.')
      return
    }
    if (file.size > MAX_PROFILE_PHOTO_BYTES) {
      setError('Image must be 5 MB or smaller.')
      return
    }
    setError(null)
    setUploading(true)
    try {
      const imageBase64 = await readFileAsBase64(file)
      await apiFetch('/crew/profile/photo', {
        method: 'POST',
        body: { imageBase64, filename: file.name },
      })
      onUploaded()
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Photo upload failed.',
      )
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleRemove() {
    setError(null)
    setRemoving(true)
    try {
      await apiFetch('/crew/profile/photo', { method: 'DELETE' })
      onUploaded()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not remove photo.')
    } finally {
      setRemoving(false)
    }
  }

  async function handleSaveUrl(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSavingUrl(true)
    try {
      await apiFetch('/crew/profile', {
        method: 'PATCH',
        body: { photoUrl: urlInput.trim() || undefined },
      })
      setUseUrl(false)
      setUrlInput('')
      onUploaded()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save photo URL.')
    } finally {
      setSavingUrl(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border border-ovation-ink/15 bg-ovation-ink/5">
        {photoUrl ? (
          <img src={photoUrl} alt="Profile" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-ovation-muted">
            <Camera className="h-8 w-8 opacity-40" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <p className="text-xs uppercase tracking-wider text-ovation-muted">Profile photo</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={PROFILE_PHOTO_TYPES.join(',')}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFile(file)
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
            {uploading ? 'Uploading…' : photoUrl ? 'Change photo' : 'Upload photo'}
          </button>
          {photoUrl && (
            <button
              type="button"
              disabled={removing}
              onClick={() => void handleRemove()}
              className="inline-flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={() => setUseUrl((v) => !v)}
            className="text-sm text-ovation-muted underline-offset-2 hover:underline"
          >
            {useUrl ? 'Hide URL option' : 'Use image URL instead'}
          </button>
        </div>
        <p className="text-xs text-ovation-muted">JPEG, PNG, WebP, or GIF — max 5 MB.</p>
        {useUrl && (
          <form onSubmit={handleSaveUrl} className="flex flex-wrap items-end gap-2">
            <div className="min-w-[200px] flex-1">
              <label htmlFor="photoUrlFallback" className="mb-1 block text-xs text-ovation-muted">
                Image URL
              </label>
              <input
                id="photoUrlFallback"
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://…"
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={savingUrl || !urlInput.trim()}
              className="rounded-md bg-ovation-ink px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              {savingUrl ? 'Saving…' : 'Save URL'}
            </button>
          </form>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  )
}

// ─── Section 2: Location & Availability ──────────────────────────────────────

function LocationAvailabilitySection({
  profile,
  onSaved,
}: {
  profile: CrewProfile
  onSaved: () => void
}) {
  const [baseCity, setBaseCity] = useState(profile.baseCity)
  const [baseCountry, setBaseCountry] = useState(profile.baseCountry)
  const [willingToTravel, setWillingToTravel] = useState(profile.willingToTravel)
  const [travelRadiusKm, setTravelRadiusKm] = useState(
    profile.travelRadiusKm != null ? String(profile.travelRadiusKm) : '',
  )
  const [dayRate, setDayRate] = useState(
    profile.dayRateMinor != null ? String(profile.dayRateMinor / 100) : '',
  )
  const [currency, setCurrency] = useState(profile.currency)
  const [publicProfile, setPublicProfile] = useState(profile.publicProfile)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setBaseCity(profile.baseCity)
    setBaseCountry(profile.baseCountry)
    setWillingToTravel(profile.willingToTravel)
    setTravelRadiusKm(profile.travelRadiusKm != null ? String(profile.travelRadiusKm) : '')
    setDayRate(profile.dayRateMinor != null ? String(profile.dayRateMinor / 100) : '')
    setCurrency(profile.currency)
    setPublicProfile(profile.publicProfile)
  }, [profile])

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        baseCity,
        baseCountry,
        willingToTravel,
        currency,
        publicProfile,
      }
      const radius = parseInt(travelRadiusKm, 10)
      if (!Number.isNaN(radius) && radius > 0) body.travelRadiusKm = radius
      const rate = parseFloat(dayRate)
      if (!Number.isNaN(rate)) body.dayRateMinor = Math.round(rate * 100)
      await apiFetch('/crew/profile', { method: 'PATCH', body })
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleSave}
      className="rounded-lg border border-ovation-ink/10 bg-white p-6"
    >
      <h2 className="mb-4 text-xs uppercase tracking-wider text-ovation-muted">
        Location & Availability
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="baseCity" label="Base city" value={baseCity} onChange={setBaseCity} required />
        <CountrySelect
          id="baseCountry"
          label="Base country"
          value={baseCountry}
          onChange={setBaseCountry}
          required
        />
        <div className="flex items-center gap-3">
          <Toggle
            id="willingToTravel"
            label="Willing to travel"
            checked={willingToTravel}
            onChange={setWillingToTravel}
          />
        </div>
        {willingToTravel && (
          <Field
            id="travelRadiusKm"
            label="Travel radius (km)"
            type="number"
            value={travelRadiusKm}
            onChange={setTravelRadiusKm}
          />
        )}
        <Field
          id="dayRate"
          label="Day rate"
          type="number"
          value={dayRate}
          onChange={setDayRate}
          hint="In major currency units"
        />
        <div>
          <label htmlFor="currency" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Currency
          </label>
          <select
            id="currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Toggle
            id="publicProfile"
            label="Public profile"
            checked={publicProfile}
            onChange={setPublicProfile}
            icon={publicProfile ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          />
        </div>
      </div>
      <FormFooter saving={saving} error={error} saved={saved} />
    </form>
  )
}

// ─── Section 3: Travel Documents ─────────────────────────────────────────────

function TravelDocumentsSection() {
  const { data, isLoading } = useQuery<TravelDocuments | null, ApiClientError>({
    queryKey: ['crew-travel-docs'],
    queryFn: async () => {
      const res = await apiFetch<TravelDocuments | null>('/crew/profile/travel-documents')
      return res
    },
    retry: false,
  })

  const [passportHeld, setPassportHeld] = useState(false)
  const [passportCountry, setPassportCountry] = useState('')
  const [passportExpiry, setPassportExpiry] = useState('')
  const [secondPassportHeld, setSecondPassportHeld] = useState(false)
  const [secondPassportCountry, setSecondPassportCountry] = useState('')
  const [secondPassportExpiry, setSecondPassportExpiry] = useState('')
  const [hasEntryRestrictions, setHasEntryRestrictions] = useState(false)
  const [restrictedCountries, setRestrictedCountries] = useState<string[]>([])
  const [drivingLicenceHeld, setDrivingLicenceHeld] = useState(false)
  const [drivingLicenceCountry, setDrivingLicenceCountry] = useState('')
  const [drivingCategories, setDrivingCategories] = useState<string[]>([])
  const [additionalLicenceCountries, setAdditionalLicenceCountries] = useState<string[]>([])
  const [internationalPermit, setInternationalPermit] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restrictedCountriesError, setRestrictedCountriesError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!data) return
    setPassportHeld(data.passportHeld)
    setPassportCountry(data.passportCountry ?? '')
    setPassportExpiry(data.passportExpiry ? data.passportExpiry.split('T')[0] ?? '' : '')
    setSecondPassportHeld(data.secondPassportHeld)
    setSecondPassportCountry(data.secondPassportCountry ?? '')
    setSecondPassportExpiry(data.secondPassportExpiry ? data.secondPassportExpiry.split('T')[0] ?? '' : '')
    setHasEntryRestrictions(data.hasEntryRestrictions)
    setRestrictedCountries(data.restrictedCountries)
    setDrivingLicenceHeld(data.drivingLicenceHeld)
    setDrivingLicenceCountry(data.drivingLicenceCountry ?? '')
    setDrivingCategories(data.drivingCategories)
    setAdditionalLicenceCountries(data.additionalLicenceCountries)
    setInternationalPermit(data.internationalPermit)
  }, [data])

  function toggleCategory(cat: string) {
    setDrivingCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    )
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setRestrictedCountriesError(null)
    setSaved(false)

    if (hasEntryRestrictions && restrictedCountries.length === 0) {
      setRestrictedCountriesError('Add at least one country, or turn this off.')
      return
    }

    setSaving(true)
    try {
      await apiFetch('/crew/profile/travel-documents', {
        method: 'PUT',
        body: {
          passportHeld,
          passportCountry: passportCountry || undefined,
          passportExpiry: passportExpiry || undefined,
          secondPassportHeld,
          secondPassportCountry: secondPassportCountry || undefined,
          secondPassportExpiry: secondPassportExpiry || undefined,
          hasEntryRestrictions,
          restrictedCountries: hasEntryRestrictions ? restrictedCountries : [],
          drivingLicenceHeld,
          drivingLicenceCountry: drivingLicenceCountry || undefined,
          drivingCategories,
          additionalLicenceCountries,
          internationalPermit,
        },
      })
      setSaved(true)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
        <p className="text-sm text-ovation-muted">Loading travel documents...</p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSave}
      className="rounded-lg border border-ovation-ink/10 bg-white p-6"
    >
      <h2 className="mb-4 flex items-center gap-2 text-xs uppercase tracking-wider text-ovation-muted">
        <PlaneIcon className="h-4 w-4" /> Travel Documents
      </h2>

      {/* Passport */}
      <div className="mb-6 space-y-3">
        <h3 className="flex items-center gap-2 text-sm font-medium">
          <Shield className="h-4 w-4" /> Passport
        </h3>
        <Toggle id="passportHeld" label="Passport held" checked={passportHeld} onChange={setPassportHeld} />
        {passportHeld && (
          <div className="ml-6 grid gap-3 sm:grid-cols-2">
            <CountrySelect
              id="passportCountry"
              label="Country of issue"
              value={passportCountry}
              onChange={setPassportCountry}
            />
            <Field id="passportExpiry" label="Expiry date" type="date" value={passportExpiry} onChange={setPassportExpiry} />
          </div>
        )}
        <Toggle id="secondPassportHeld" label="Second passport" checked={secondPassportHeld} onChange={setSecondPassportHeld} />
        {secondPassportHeld && (
          <div className="ml-6 grid gap-3 sm:grid-cols-2">
            <CountrySelect
              id="secondPassportCountry"
              label="Country of issue"
              value={secondPassportCountry}
              onChange={setSecondPassportCountry}
            />
            <Field id="secondPassportExpiry" label="Expiry date" type="date" value={secondPassportExpiry} onChange={setSecondPassportExpiry} />
          </div>
        )}
      </div>

      {/* Entry restrictions */}
      <div className="mb-6 space-y-3">
        <h3 className="flex items-center gap-2 text-sm font-medium">
          <Globe className="h-4 w-4" /> Travel Restrictions
        </h3>
        <Toggle
          id="hasEntryRestrictions"
          label="Are there any countries you may have difficulty entering?"
          checked={hasEntryRestrictions}
          onChange={(on) => {
            setHasEntryRestrictions(on)
            setRestrictedCountriesError(null)
            if (!on) setRestrictedCountries([])
          }}
        />
        {hasEntryRestrictions && (
          <div className="ml-6 space-y-2 rounded-md border border-ovation-ink/10 bg-ovation-paper/40 p-4">
            <CountryMultiSelect
              id="restrictedCountries"
              label="Restricted countries"
              values={restrictedCountries}
              onChange={(next) => {
                setRestrictedCountries(next)
                if (next.length > 0) setRestrictedCountriesError(null)
              }}
              addOnSelect
              required
              error={restrictedCountriesError}
              hint="Select each country from the list — one at a time."
            />
            <p className="text-xs text-ovation-muted italic">
              This information is confidential and only shared with tour managers for active engagements.
            </p>
          </div>
        )}
      </div>

      {/* Driving */}
      <div className="space-y-3">
        <h3 className="flex items-center gap-2 text-sm font-medium">
          <Car className="h-4 w-4" /> Driving
        </h3>
        <Toggle id="drivingLicenceHeld" label="Driving licence held" checked={drivingLicenceHeld} onChange={setDrivingLicenceHeld} />
        {drivingLicenceHeld && (
          <div className="ml-6 space-y-3">
            <CountrySelect
              id="drivingLicenceCountry"
              label="Country of issue"
              value={drivingLicenceCountry}
              onChange={setDrivingLicenceCountry}
            />
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
                Categories
              </label>
              <div className="flex gap-3">
                {DRIVING_CATEGORIES.map((cat) => (
                  <label key={cat} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={drivingCategories.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                      className="rounded border-ovation-ink/15"
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>
            <CountryMultiSelect
              id="additionalLicenceCountries"
              label="Additional licence countries"
              values={additionalLicenceCountries}
              onChange={setAdditionalLicenceCountries}
            />
            <Toggle id="internationalPermit" label="International driving permit" checked={internationalPermit} onChange={setInternationalPermit} />
          </div>
        )}
      </div>

      <FormFooter saving={saving} error={error} saved={saved} />
    </form>
  )
}

// ─── Section 4: Work History ─────────────────────────────────────────────────

function WorkHistorySection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery<WorkHistoryEntry[], ApiClientError>({
    queryKey: ['crew-work-history'],
    queryFn: () => apiFetch<WorkHistoryEntry[]>('/crew/profile/work-history'),
    retry: false,
  })

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['crew-work-history'] })
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this work history entry?')) return
    await apiFetch(`/crew/profile/work-history/${id}`, { method: 'DELETE' })
    refresh()
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wider text-ovation-muted">
          Work History
        </h2>
        <button
          type="button"
          onClick={() => { setShowForm(true); setEditingId(null) }}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-3 w-3" /> Add entry
        </button>
      </div>

      {isLoading && <p className="text-sm text-ovation-muted">Loading...</p>}

      {showForm && (
        <WorkHistoryForm
          onDone={() => { setShowForm(false); refresh() }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingId && (
        <WorkHistoryForm
          entry={(data ?? []).find((e) => e.id === editingId)!}
          onDone={() => { setEditingId(null); refresh() }}
          onCancel={() => setEditingId(null)}
        />
      )}

      <div className="mt-4 space-y-3">
        {(data ?? []).map((entry) => (
          <div
            key={entry.id}
            className="flex items-start justify-between rounded-md border border-ovation-ink/5 p-3"
          >
            <div>
              <p className="text-sm font-medium">{entry.title}</p>
              <p className="text-xs text-ovation-muted">
                {roleLabel(entry.role)}
                {entry.artistName && entry.showArtistName && ` \u00b7 ${entry.artistName}`}
                {entry.city && ` \u00b7 ${entry.city}`}
                {entry.country && `, ${entry.country}`}
              </p>
              {entry.startDate && (
                <p className="text-xs text-ovation-muted">
                  {entry.startDate.split('T')[0]}
                  {entry.endDate && ` \u2013 ${entry.endDate.split('T')[0]}`}
                </p>
              )}
              {entry.description && (
                <p className="mt-1 text-xs text-ovation-muted">{entry.description}</p>
              )}
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => { setEditingId(entry.id); setShowForm(false) }}
                className="rounded p-1 text-ovation-muted hover:text-ovation-ink"
              >
                <Save className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(entry.id)}
                className="rounded p-1 text-ovation-muted hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function WorkHistoryForm({
  entry,
  onDone,
  onCancel,
}: {
  entry?: WorkHistoryEntry
  onDone: () => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(entry?.title ?? '')
  const [artistName, setArtistName] = useState(entry?.artistName ?? '')
  const [role, setRole] = useState(entry?.role ?? 'foh_engineer')
  const [startDate, setStartDate] = useState(entry?.startDate?.split('T')[0] ?? '')
  const [endDate, setEndDate] = useState(entry?.endDate?.split('T')[0] ?? '')
  const [city, setCity] = useState(entry?.city ?? '')
  const [country, setCountry] = useState(entry?.country ?? '')
  const [description, setDescription] = useState(entry?.description ?? '')
  const [showArtistName, setShowArtistName] = useState(entry?.showArtistName ?? true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        title,
        role,
        showArtistName,
      }
      if (artistName) body.artistName = artistName
      if (startDate) body.startDate = startDate
      if (endDate) body.endDate = endDate
      if (city) body.city = city
      if (country) body.country = country
      if (description) body.description = description

      if (entry) {
        await apiFetch(`/crew/profile/work-history/${entry.id}`, { method: 'PATCH', body })
      } else {
        await apiFetch('/crew/profile/work-history', { method: 'POST', body })
      }
      onDone()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mb-4 space-y-3 rounded-md border border-ovation-ink/10 bg-ovation-paper/50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="wh-title" label="Title" value={title} onChange={setTitle} required />
        <Field id="wh-artist" label="Artist name" value={artistName} onChange={setArtistName} />
        <div>
          <label htmlFor="wh-role" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Role
          </label>
          <select
            id="wh-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            {SHOW_ROLES.map((r) => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Toggle id="wh-showArtist" label="Show artist name on profile" checked={showArtistName} onChange={setShowArtistName} />
        </div>
        <Field id="wh-start" label="Start date" type="date" value={startDate} onChange={setStartDate} />
        <Field id="wh-end" label="End date" type="date" value={endDate} onChange={setEndDate} />
        <Field id="wh-city" label="City" value={city} onChange={setCity} />
        <CountrySelect id="wh-country" label="Country" value={country} onChange={setCountry} />
        <div className="sm:col-span-2">
          <label htmlFor="wh-desc" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Description
          </label>
          <textarea
            id="wh-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Save className="h-3 w-3" /> {saving ? 'Saving...' : entry ? 'Update' : 'Add'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

// ─── Section 5: Testimonials ─────────────────────────────────────────────────

function TestimonialsSection() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery<Testimonial[], ApiClientError>({
    queryKey: ['crew-testimonials'],
    queryFn: () => apiFetch<Testimonial[]>('/crew/profile/testimonials'),
    retry: false,
  })

  const [showForm, setShowForm] = useState(false)

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['crew-testimonials'] })
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this testimonial?')) return
    await apiFetch(`/crew/profile/testimonials/${id}`, { method: 'DELETE' })
    refresh()
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wider text-ovation-muted">
          Testimonials
        </h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-3 w-3" /> Add testimonial
        </button>
      </div>

      {isLoading && <p className="text-sm text-ovation-muted">Loading...</p>}

      {showForm && (
        <TestimonialForm
          onDone={() => { setShowForm(false); refresh() }}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className="mt-4 space-y-3">
        {(data ?? []).map((t) => (
          <div
            key={t.id}
            className="rounded-md border border-ovation-ink/5 p-3"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm italic">&ldquo;{t.text}&rdquo;</p>
                <p className="mt-1 text-xs font-medium">
                  &mdash; {t.fromName}
                  {t.fromRole && <span className="text-ovation-muted"> ({t.fromRole})</span>}
                </p>
                <div className="mt-1 flex items-center gap-2 text-xs text-ovation-muted">
                  {t.eventName && <span>{t.eventName}</span>}
                  {t.eventDate && <span>{t.eventDate.split('T')[0]}</span>}
                  {t.rating != null && (
                    <span className="flex items-center gap-0.5">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {t.rating}/5
                    </span>
                  )}
                  {t.publicVisible ? (
                    <span className="flex items-center gap-0.5"><Eye className="h-3 w-3" /> Public</span>
                  ) : (
                    <span className="flex items-center gap-0.5"><EyeOff className="h-3 w-3" /> Private</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(t.id)}
                className="rounded p-1 text-ovation-muted hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* External testimonial request section */}
      <TestimonialRequestSection />
    </div>
  )
}

// ─── Testimonial Request Section ─────────────────────────────────────────────

interface TestimonialRequestItem {
  id: string
  recipientEmail: string
  recipientName: string | null
  claimedRole: string
  eventName: string | null
  status: string
  createdAt: string
}

function TestimonialRequestSection() {
  const queryClient = useQueryClient()
  const [showRequestForm, setShowRequestForm] = useState(false)

  const { data: requests } = useQuery<TestimonialRequestItem[], ApiClientError>({
    queryKey: ['testimonial-requests'],
    queryFn: () => apiFetch<TestimonialRequestItem[]>('/crew/profile/testimonial-requests'),
    retry: false,
  })

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['testimonial-requests'] })
  }

  return (
    <div className="mt-6 border-t border-ovation-ink/10 pt-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-ovation-muted">
          External testimonial requests
        </h3>
        <button
          type="button"
          onClick={() => setShowRequestForm(!showRequestForm)}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-3 w-3" /> Request external testimonial
        </button>
      </div>

      {showRequestForm && (
        <TestimonialRequestForm
          onDone={() => { setShowRequestForm(false); refresh() }}
          onCancel={() => setShowRequestForm(false)}
        />
      )}

      {(requests ?? []).length > 0 && (
        <div className="mt-3 space-y-2">
          {(requests ?? []).map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-md border border-ovation-ink/5 px-3 py-2">
              <div className="text-sm">
                <span className="font-medium">{r.recipientName ?? r.recipientEmail}</span>
                <span className="ml-2 text-xs text-ovation-muted">({r.claimedRole})</span>
                {r.eventName && <span className="ml-2 text-xs text-ovation-muted">{r.eventName}</span>}
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                r.status === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : r.status === 'expired'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
              }`}>
                {r.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TestimonialRequestForm({
  onDone,
  onCancel,
}: {
  onDone: () => void
  onCancel: () => void
}) {
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [claimedRole, setClaimedRole] = useState('')
  const [eventName, setEventName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        recipientEmail,
        claimedRole,
      }
      if (recipientName) body.recipientName = recipientName
      if (eventName) body.eventName = eventName

      await apiFetch('/crew/profile/testimonial-requests', { method: 'POST', body })
      onDone()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mb-4 space-y-3 rounded-md border border-ovation-ink/10 bg-ovation-paper/50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="tr-email" label="Recipient email" type="email" value={recipientEmail} onChange={setRecipientEmail} required />
        <Field id="tr-name" label="Recipient name" value={recipientName} onChange={setRecipientName} hint="Optional" />
        <Field id="tr-role" label="What role did you perform?" value={claimedRole} onChange={setClaimedRole} required hint="e.g. Lighting Tech, FOH Engineer" />
        <Field id="tr-event" label="Event name" value={eventName} onChange={setEventName} hint="Optional, e.g. 2025 European Tour" />
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {saving ? 'Sending...' : 'Send request'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function TestimonialForm({
  onDone,
  onCancel,
}: {
  onDone: () => void
  onCancel: () => void
}) {
  const [fromName, setFromName] = useState('')
  const [fromRole, setFromRole] = useState('')
  const [text, setText] = useState('')
  const [rating, setRating] = useState('')
  const [eventName, setEventName] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [publicVisible, setPublicVisible] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        fromName,
        text,
        publicVisible,
      }
      if (fromRole) body.fromRole = fromRole
      const r = parseInt(rating, 10)
      if (!Number.isNaN(r) && r >= 1 && r <= 5) body.rating = r
      if (eventName) body.eventName = eventName
      if (eventDate) body.eventDate = eventDate

      await apiFetch('/crew/profile/testimonials', { method: 'POST', body })
      onDone()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mb-4 space-y-3 rounded-md border border-ovation-ink/10 bg-ovation-paper/50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="t-fromName" label="From (name)" value={fromName} onChange={setFromName} required />
        <Field id="t-fromRole" label="From (role)" value={fromRole} onChange={setFromRole} />
        <div className="sm:col-span-2">
          <label htmlFor="t-text" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Testimonial text
          </label>
          <textarea
            id="t-text"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <Field id="t-rating" label="Rating (1-5)" type="number" value={rating} onChange={setRating} hint="Optional star rating" />
        <Field id="t-eventName" label="Event name" value={eventName} onChange={setEventName} />
        <Field id="t-eventDate" label="Event date" type="date" value={eventDate} onChange={setEventDate} />
        <div className="flex items-end">
          <Toggle id="t-public" label="Publicly visible" checked={publicVisible} onChange={setPublicVisible} />
        </div>
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" /> {saving ? 'Adding...' : 'Add testimonial'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

// ─── Create Profile (when none exists) ───────────────────────────────────────

function CrewProfileCreate({ onCreated }: { onCreated: () => void }) {
  const [displayName, setDisplayName] = useState('')
  const [primaryRole, setPrimaryRole] = useState<string>('foh_engineer')
  const [baseCity, setBaseCity] = useState('')
  const [baseCountry, setBaseCountry] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await apiFetch('/crew/profile', {
        method: 'POST',
        body: { displayName, primaryRole, baseCity, baseCountry },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Create your crew profile"
        subtitle="Tell artists who you are so they can book you."
      />
      <form
        onSubmit={onSubmit}
        className="max-w-lg space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6"
      >
        <Field id="displayName" label="Display name" value={displayName} onChange={setDisplayName} required />
        <div>
          <label htmlFor="primaryRole" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Primary role
          </label>
          <select
            id="primaryRole"
            value={primaryRole}
            onChange={(e) => setPrimaryRole(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          >
            {SHOW_ROLES.map((r) => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
        </div>
        <Field id="baseCity" label="Base city" value={baseCity} onChange={setBaseCity} required />
        <CountrySelect
          id="create-baseCountry"
          label="Base country"
          value={baseCountry}
          onChange={setBaseCountry}
          required
        />
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {submitting ? 'Creating...' : 'Create profile'}
        </button>
      </form>
    </div>
  )
}

// ─── Section 6: Stripe Payments ──────────────────────────────────────────────

function StripePaymentsSection() {
  const queryClient = useQueryClient()
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check URL params for Stripe return/refresh
  const urlParams = new URLSearchParams(window.location.search)
  const stripeParam = urlParams.get('stripe')

  const { data: status, isLoading: statusLoading } = useQuery<StripeStatus, ApiClientError>({
    queryKey: ['crew-stripe-status'],
    queryFn: () => apiFetch<StripeStatus>('/crew/profile/stripe/status'),
    retry: false,
    // Re-fetch on mount if returning from Stripe onboarding
    refetchOnMount: stripeParam === 'connected' || stripeParam === 'refresh',
  })

  const { data: payments } = useQuery<CrewPaymentRecord[], ApiClientError>({
    queryKey: ['crew-stripe-payments'],
    queryFn: () => apiFetch<CrewPaymentRecord[]>('/crew/profile/stripe/payments'),
    retry: false,
    enabled: !!status?.connected,
  })

  async function handleConnect() {
    setError(null)
    setConnecting(true)
    try {
      const result = await apiFetch<{ url: string; accountId: string }>(
        '/crew/profile/stripe/connect',
        { method: 'POST', body: { country: 'GB' } },
      )
      window.location.href = result.url
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Failed to start Stripe setup',
      )
      setConnecting(false)
    }
  }

  async function handleDashboard() {
    try {
      const result = await apiFetch<{ url: string }>('/crew/profile/stripe/dashboard')
      window.open(result.url, '_blank')
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Failed to open dashboard',
      )
    }
  }

  function formatMoney(pence: number, currency = 'GBP'): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(pence / 100)
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <Banknote className="h-4 w-4 text-ovation-muted" />
        <h2 className="text-xs uppercase tracking-wider text-ovation-muted">
          Payments
        </h2>
      </div>

      {statusLoading && (
        <p className="text-sm text-ovation-muted">Checking payment status...</p>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {stripeParam === 'connected' && status?.onboardingComplete && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Stripe account connected successfully. You can now receive payments.
        </div>
      )}

      {stripeParam === 'refresh' && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Your Stripe onboarding session expired. Click below to continue setup.
        </div>
      )}

      {/* Not connected */}
      {!statusLoading && !status?.connected && (
        <div className="space-y-3">
          <p className="text-sm text-ovation-muted">
            Connect your Stripe account to receive payments directly from artists
            for your engagements.
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {connecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Banknote className="h-4 w-4" />
            )}
            {connecting ? 'Setting up...' : 'Connect your Stripe account'}
          </button>
        </div>
      )}

      {/* Connected but onboarding incomplete */}
      {!statusLoading && status?.connected && !status.onboardingComplete && (
        <div className="space-y-3">
          <p className="text-sm text-amber-700">
            Your Stripe account setup is not yet complete. Please finish the
            onboarding process to receive payments.
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {connecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
            {connecting ? 'Redirecting...' : 'Complete your Stripe setup'}
          </button>
        </div>
      )}

      {/* Fully connected */}
      {!statusLoading && status?.connected && status.onboardingComplete && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
              <CheckCircle className="h-3 w-3" />
              Payments enabled
            </span>
            <button
              type="button"
              onClick={handleDashboard}
              className="flex items-center gap-1 text-xs font-medium text-ovation-ink hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> Stripe dashboard
            </button>
          </div>

          {/* Payment history */}
          {(payments ?? []).length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-xs uppercase tracking-wider text-ovation-muted">
                Payment history
              </h3>
              <div className="space-y-2">
                {(payments ?? []).map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-md border border-ovation-ink/5 px-3 py-2"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium">{p.description}</div>
                      <div className="text-xs text-ovation-muted">
                        {p.paidAt
                          ? new Date(p.paidAt).toLocaleDateString()
                          : new Date(p.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">
                        {formatMoney(p.netPence, p.currency)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          p.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : p.status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : p.status === 'processing'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(payments ?? []).length === 0 && (
            <p className="text-sm text-ovation-muted">
              No payments received yet. Once an artist sends you a payment, it
              will appear here.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Shared UI Primitives ────────────────────────────────────────────────────

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  required?: boolean
  hint?: string
}

function Field(props: FieldProps) {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
        {props.label}
      </label>
      <input
        id={props.id}
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        required={props.required}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
      />
      {props.hint && <p className="mt-1 text-xs text-ovation-muted">{props.hint}</p>}
    </div>
  )
}

function Toggle({
  id,
  label,
  checked,
  onChange,
  icon,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  icon?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <label htmlFor={id} className="flex flex-1 cursor-pointer items-center gap-2 text-sm">
        {icon}
        {label}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-ovation-ink' : 'bg-ovation-ink/15'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  )
}

function FormFooter({
  saving,
  error,
  saved,
}: {
  saving: boolean
  error: string | null
  saved: boolean
}) {
  return (
    <div className="mt-5">
      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {saved && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Saved.
        </div>
      )}
      <button
        type="submit"
        disabled={saving}
        className="flex items-center gap-1.5 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  )
}
